/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import { CmdBuilder, IViewModel } from "@nuance/hap-components";
import { IStartViewState } from "./StartViewViewModel";

export interface ICreateNewDefinitionState {
    canSave: boolean;
    settingDefinitionName: string;
    settingDefinitionErrorMsg: string;
    evaluationMethod: string;
}

export class CreateNewDefinitionCommands {
    readonly SettingDefinitionNameRegex = new RegExp(
        "^[a-zA-Z]$|^[a-zA-Z0-9]+[a-zA-Z0-9-_]*[a-zA-Z0-9]+$"
    );

    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            definitionNameChanged: cb.bindCmd<ICreateNewDefinitionState, string>(
                this.definitionNameChangedHandler
            ),
            nameBlur: cb.bindCmdNP(this.nameBlurHandler),
            evaluationMethodChange: cb.bindCmd<ICreateNewDefinitionState, string>(
                this.evaluationMethodChangeHandler
            )
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Handles definition name changed
     * @param state Old state
     * @param newValue New definition name
     * @returns New state
     */
    definitionNameChangedHandler = (state: ICreateNewDefinitionState, newValue: string) => {
        state.settingDefinitionName = newValue;
        state.canSave = true;

        // validation
        if (newValue === "" || this.validateSettingName(newValue).length > 0) {
            state.canSave = false;
        }

        return state;
    };

    nameBlurHandler = (state: ICreateNewDefinitionState) => {
        state.settingDefinitionErrorMsg = this.validateSettingName(state.settingDefinitionName);
        state.canSave =
            state.settingDefinitionName.length > 0 && state.settingDefinitionErrorMsg.length === 0;
        return state;
    };

    /**
     * Handles evaluation method change.
     * @param state Old State
     * @param newValue New evaluation method
     * @returns New state
     */
    evaluationMethodChangeHandler = (state: ICreateNewDefinitionState, newValue: string) => {
        state.evaluationMethod = newValue;
        return state;
    };

    /**
     * Validates the setting name
     * @param name Setting name
     * @returns Error message or blank string
     */
    private validateSettingName = (name: string) => {
        let errorMsg = "";

        if (name.length > 0) {
            if (name.length < 5) {
                errorMsg = "SettingDefinitionNameTooShort";
            } else if (!this.SettingDefinitionNameRegex.test(name)) {
                errorMsg = "SettingDefinitionHasInvalidCharacters";
            }
        }

        return errorMsg;
    };
}

type ICreateNewDefinitionCmds = CreateNewDefinitionCommands["Cmds"];

export type ICreateNewDefinitionViewModel = IViewModel<IStartViewState, ICreateNewDefinitionCmds>;
